import { Link } from "react-router-dom";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from "recharts";
import { useFetch, fmtDate } from "@/lib/api";
import { Panel, Badge, Stat, Empty } from "@/components/common";

const Tiles = ({ r }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="results-tiles">
    <Stat label="Emails sent" value={r.emails.sent + r.emails.simulated} sub={r.emails.test_copies ? `+${r.emails.test_copies} test copies` : ""} testid="results-sent" />
    <Stat label="Replies" value={r.replies} tone="blue" testid="results-replies" />
    <Stat label="Meetings" value={r.meetings} tone="green" testid="results-meetings" />
    <Stat label="Opt-outs" value={r.opt_outs} tone="amber" testid="results-optouts" />
    <Stat label="Bounces" value={r.bounces} tone="red" testid="results-bounces" />
    <Stat label="Blocked / failed" value={r.emails.blocked + r.emails.failed} testid="results-blocked" />
    <Stat label="Newsletter sent" value={r.newsletter.sent + r.newsletter.simulated + r.newsletter.test_copies} testid="results-newsletter" />
    <Stat label="Link clicks" value={Object.values(r.clicks).reduce((a, c) => a + c.clicks, 0)} testid="results-clicks" />
  </div>
);

const Chart = ({ days }) => (
  <Panel title="Day by day">
    <div className="h-56" data-testid="results-chart">
      <ResponsiveContainer>
        <BarChart data={days}>
          <XAxis dataKey="day" tick={{ fill: "#71717A", fontSize: 11 }} />
          <YAxis allowDecimals={false} tick={{ fill: "#71717A", fontSize: 11 }} width={32} />
          <Tooltip contentStyle={{ background: "#121212", border: "1px solid #27272A" }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="sent" name="Emails sent" fill="#7C3AED" radius={[2, 2, 0, 0]} />
          <Bar dataKey="replies" name="Replies" fill="#F97316" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </Panel>
);

const Breakdown = ({ r }) => (
  <Panel title="Breakdown">
    <div className="space-y-4 text-xs" data-testid="results-breakdown">
      {[["Sequence progress", r.enrollments], ["Reply types", r.intents],
        ["Clicks by channel", Object.fromEntries(Object.entries(r.clicks).map(([k, v]) => [k, `${v.clicks} (${v.people} people)`]))]].map(([t, obj]) => (
        <div key={t}>
          <div className="font-mono uppercase tracking-wider text-zinc-500 mb-1">{t}</div>
          {Object.keys(obj).length ? Object.entries(obj).map(([k, v]) => (
            <div key={k} className="flex justify-between py-1 border-b border-border/50"><span className="text-zinc-400">{k.replace(/_/g, " ")}</span><span className="font-mono">{v}</span></div>
          )) : <div className="text-zinc-600">None yet</div>}
        </div>
      ))}
    </div>
  </Panel>
);

const Replies = ({ list }) => (
  <Panel title="Latest replies">
    {!list.length ? <Empty>No replies yet</Empty> : (
      <div className="divide-y divide-border/60" data-testid="results-latest-replies">{list.map((x, i) => (
        <Link key={i} to={`/inbox?thread=${x.thread_id}`} data-testid={`results-reply-${i}`} className="block py-3 hover:bg-[#1A1A1A] px-2 -mx-2 rounded transition-colors">
          <div className="flex items-center gap-2 text-sm"><span className="font-medium">{x.name || "Unknown"}</span><span className="text-zinc-500 truncate">{x.company}</span><Badge v={x.intent} className="ml-auto" /></div>
          <div className="text-xs text-zinc-400 mt-1 line-clamp-2">{x.snippet}</div>
          <div className="text-[10px] font-mono text-zinc-600 mt-1">{fmtDate(x.at)}</div>
        </Link>
      ))}</div>
    )}
  </Panel>
);

export const LaunchResults = ({ id }) => {
  const { data: r } = useFetch(`/launches/${id}/results`);
  if (!r?.launched) return null;
  return (
    <section className="space-y-4" data-testid="launch-results">
      <h2 className="text-lg font-bold">Results since launch <span className="text-xs font-mono text-zinc-500 font-normal">{fmtDate(r.since)}</span></h2>
      <Tiles r={r} />
      <div className="grid xl:grid-cols-[1fr_360px] gap-4"><Chart days={r.daily} /><Breakdown r={r} /></div>
      <Replies list={r.latest_replies} />
    </section>
  );
};
